import React, { useState, useEffect, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import { createWorkspaceInLambda } from '../utils/lambdaApi';
import { editUserPermissions, fetchUserPermissions, fetchUsersByBoardCode } from '../utils/api/permissionsApi';
import { BOARD_PERMISSION_KEYS, BOARD_RESTRICTION_LABELS, getDefaultBoardPermissions } from '../utils/permissionKeys.js';
import { LABS_API_URL, DEFAULT_HEADERS } from '../utils/config';

// Ya no usamos emoticonos para los tableros
// eslint-disable-next-line no-unused-vars
const AVAILABLE_ICONS = [''];

function WorkspaceManager({ onClose }) {
  const { workspaceData, addWorkspace, updateWorkspace, deleteWorkspace, workspacesLoading } = useKanban();
  const { apiLoading, apiError, userData } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('');
  const [editWorkspaceName, setEditWorkspaceName] = useState('');
  const [editWorkspaceIcon, setEditWorkspaceIcon] = useState('');
  const [editWorkspaceRestrictions, setEditWorkspaceRestrictions] = useState(() => getDefaultBoardPermissions());
  const [newWorkspaceRestrictions, setNewWorkspaceRestrictions] = useState(() => getDefaultBoardPermissions());
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [currentStep, setCurrentStep] = useState(2); // Siempre mostrar paso 2 después de crear
  // eslint-disable-next-line no-unused-vars
  const [generatedCode, setGeneratedCode] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [createdWorkspaceCode, setCreatedWorkspaceCode] = useState(null);
  
  // Estados para añadir/quitar personas
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [selectedUsersToRemove, setSelectedUsersToRemove] = useState([]);
  const [currentBoardUsers, setCurrentBoardUsers] = useState([]); // Usuarios que ya tienen acceso
  const [addUserSearchTerm, setAddUserSearchTerm] = useState('');
  const [removeUserSearchTerm, setRemoveUserSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [filteredUsersToAdd, setFilteredUsersToAdd] = useState([]);
  const [filteredUsersToRemove, setFilteredUsersToRemove] = useState([]);
  
  // Nuevos estados para el buscador
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);

  // Función para filtrar los tableros según el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkspaces(workspaceData);
    } else {
      const filtered = workspaceData.filter(workspace =>
        workspace.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWorkspaces(filtered);
    }
  }, [searchTerm, workspaceData]);

  // Función para obtener el código de departamento del perfil de usuario
  const getDepartmentCode = () => {
    if (!userData || !userData.department) return '';
    
    // Extraer el código numérico al inicio del departamento (ej: "14337 Nombre Depto")
    const departmentCodeMatch = userData.department.match(/^(\d+)/);
    return departmentCodeMatch ? departmentCodeMatch[1] : '';
  };

  // Función para generar un hash aleatorio
  const generateRandomHash = () => {
    // Genera un hash aleatorio de 16 caracteres usando caracteres alfanuméricos
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let hash = '';
    for (let i = 0; i < 16; i++) {
      hash += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return hash;
  };

  // Cargar usuarios cuando se entra al modo crear o editar
  useEffect(() => {
    if ((isCreating || isEditing) && allUsers.length === 0 && !loadingUsers) {
      fetchAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, isEditing]);

  // Cargar usuarios actuales del tablero cuando se edita
  useEffect(() => {
    const loadCurrentBoardUsers = async () => {
      if (!isEditing) {
        setCurrentBoardUsers([]);
        return;
      }

      const workspace = workspaceData.find(ws => ws.id === isEditing);
      if (!workspace || !workspace.code) {
        setCurrentBoardUsers([]);
        return;
      }

      try {
        const userEmails = await fetchUsersByBoardCode(workspace.code);
        setCurrentBoardUsers(userEmails);
      } catch (error) {
        console.error('Error al obtener usuarios del tablero:', error);
        setCurrentBoardUsers([]);
      }
    };

    loadCurrentBoardUsers();
  }, [isEditing, workspaceData]);

  // Filtrar usuarios para añadir según el término de búsqueda
  useEffect(() => {
    if (addUserSearchTerm.trim() === '') {
      // Excluir usuarios que ya tienen acceso
      const availableUsers = allUsers.filter(user => {
        const userEmail = user.email || user.user || '';
        return !currentBoardUsers.includes(userEmail);
      });
      setFilteredUsersToAdd(availableUsers);
    } else {
      const searchLower = addUserSearchTerm.toLowerCase();
      const filtered = allUsers.filter(user => {
        const userEmail = user.email || user.user || '';
        const userName = user.name || '';
        // Excluir usuarios que ya tienen acceso
        if (currentBoardUsers.includes(userEmail)) return false;
        return userEmail.toLowerCase().includes(searchLower) || 
               userName.toLowerCase().includes(searchLower);
      });
      setFilteredUsersToAdd(filtered);
    }
  }, [addUserSearchTerm, allUsers, currentBoardUsers]);

  // Filtrar usuarios para quitar según el término de búsqueda
  useEffect(() => {
    if (removeUserSearchTerm.trim() === '') {
      // Mostrar solo usuarios que tienen acceso
      const usersWithAccess = allUsers.filter(user => {
        const userEmail = user.email || user.user || '';
        return currentBoardUsers.includes(userEmail);
      });
      setFilteredUsersToRemove(usersWithAccess);
    } else {
      const searchLower = removeUserSearchTerm.toLowerCase();
      const filtered = allUsers.filter(user => {
        const userEmail = user.email || user.user || '';
        const userName = user.name || '';
        // Solo mostrar usuarios que tienen acceso
        if (!currentBoardUsers.includes(userEmail)) return false;
        return userEmail.toLowerCase().includes(searchLower) || 
               userName.toLowerCase().includes(searchLower);
      });
      setFilteredUsersToRemove(filtered);
    }
  }, [removeUserSearchTerm, allUsers, currentBoardUsers]);

  // Función para obtener todos los usuarios
  const fetchAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${LABS_API_URL}/workspace/all-users`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        throw new Error(`Error al obtener usuarios: ${response.status}`);
      }
      
      const responseData = await response.json();
      let users = [];
      
      if (responseData && responseData.body) {
        try {
          users = JSON.parse(responseData.body);
          if (Array.isArray(users)) {
            setAllUsers(users);
            // Los filtros se actualizarán automáticamente mediante los useEffect
          }
        } catch (parseError) {
          console.error('Error al parsear usuarios:', parseError);
        }
      }
    } catch (err) {
      console.error('Error al obtener usuarios:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Toggle selección de usuario para añadir
  const toggleUserSelectionToAdd = (user) => {
    const userEmail = user.email || user.user || '';
    setSelectedUsersToAdd(prev => {
      if (prev.includes(userEmail)) {
        return prev.filter(email => email !== userEmail);
      } else {
        return [...prev, userEmail];
      }
    });
  };

  // Toggle selección de usuario para quitar
  const toggleUserSelectionToRemove = (user) => {
    const userEmail = user.email || user.user || '';
    setSelectedUsersToRemove(prev => {
      if (prev.includes(userEmail)) {
        return prev.filter(email => email !== userEmail);
      } else {
        return [...prev, userEmail];
      }
    });
  };

  // Inicializar creación - solo mostrar el modal
  const handleStartCreating = () => {
    setIsCreating(true);
    setNewWorkspaceName('');
    setNewWorkspaceRestrictions(getDefaultBoardPermissions());
    setSelectedUsersToAdd([]);
    setAddUserSearchTerm('');
    setError('');
    // Cargar usuarios si no están cargados
    if (allUsers.length === 0) {
      fetchAllUsers();
    }
  };

  // Manejar el paso 2: crear tablero y añadir permisos a usuarios
  const handleAddPermissions = async () => {
    if (!newWorkspaceName.trim()) {
      setError('Por favor, ingrese un nombre para el tablero.');
      return;
    }

    try {
      setIsCreatingWorkspace(true);
      setError('');

      // Generar hash aleatorio para el código
      const randomHash = generateRandomHash();
      
      // Obtener el código de departamento como fallback
      const departmentCode = getDepartmentCode();
      
      const permisosPayload = Object.values(newWorkspaceRestrictions).some(Boolean) ? newWorkspaceRestrictions : null;
      await createWorkspaceInLambda(null, newWorkspaceName, randomHash, departmentCode, permisosPayload);
      
      // Then update local state (no importa si retorna null, el tablero ya está creado en Lambda)
      addWorkspace(newWorkspaceName, selectedIcon);

      // Agregar permisos para cada usuario seleccionado (si hay alguno)
      if (selectedUsersToAdd.length > 0) {
        const permissionResults = await Promise.allSettled(
          selectedUsersToAdd.map(async (userEmail) => {
            try {
              // Obtener códigos actuales del usuario para preservarlos
              let currentCodes = [];
              try {
                const currentPermissions = await fetchUserPermissions(userEmail);
                currentCodes = currentPermissions.additionalCodes || [];
              } catch (fetchError) {
                // Si el usuario no existe, currentCodes será [] y se creará automáticamente
                // No es necesario loguear este error
              }
              
              // Combinar códigos existentes con el nuevo código (evitar duplicados)
              const updatedCodes = [...new Set([...currentCodes, randomHash])];
              
              // editUserPermissions maneja automáticamente el caso de usuario no encontrado
              // haciendo CREATE en lugar de UPDATE, así que no necesitamos manejar el 404
              const result = await editUserPermissions(userEmail, {
                additionalCodes: updatedCodes,
                admin: false,
                sendTasks: false
              }, currentCodes);
              
              return { success: true, userEmail, operation: result.operationType || 'update' };
            } catch (err) {
              // Solo loguear errores que no sean "usuario no encontrado" (que se maneja automáticamente)
              const errorMessage = err.message || '';
              const isUserNotFound = errorMessage.toLowerCase().includes('no encontrado') || 
                                    errorMessage.toLowerCase().includes('not found') ||
                                    errorMessage.includes('404');
              
              if (!isUserNotFound) {
                console.error(`Error al agregar permisos para ${userEmail}:`, err);
              }
              return { success: false, userEmail, error: err.message };
            }
          })
        );

        // Contar resultados exitosos
        const successful = permissionResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
        console.log(`Permisos procesados: ${successful}/${selectedUsersToAdd.length} usuario(s) exitosamente`);
      } else {
        console.log('No hay usuarios seleccionados para añadir permisos');
      }
      
      // Finalizar el proceso (el tablero ya está creado, con o sin permisos)
      handleFinish();
    } catch (error) {
      console.error("Error al crear tablero o agregar permisos:", error);
      setError(error.message || 'Error al crear el tablero o agregar permisos.');
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  // Manejar actualización de permisos (editar)
  const handleUpdatePermissions = async () => {
    if (!isEditing) return;

    const workspace = workspaceData.find(ws => ws.id === isEditing);
    if (!workspace || !workspace.code) {
      setError('No se pudo obtener el código del tablero.');
      return;
    }

    try {
      setIsCreatingWorkspace(true);
      setError('');

      // Quitar permisos
      if (selectedUsersToRemove.length > 0) {
        const removePromises = selectedUsersToRemove.map(async (userEmail) => {
          try {
            const currentPermissions = await fetchUserPermissions(userEmail);
            const currentCodes = currentPermissions.additionalCodes || [];
            const updatedCodes = currentCodes.filter(code => code !== workspace.code);
            
            await editUserPermissions(userEmail, {
              additionalCodes: updatedCodes,
              admin: false,
              sendTasks: false
            }, currentCodes);
          } catch (err) {
            console.error(`Error al quitar permisos para ${userEmail}:`, err);
          }
        });

        await Promise.all(removePromises);
      }

      // Añadir permisos
      if (selectedUsersToAdd.length > 0) {
        const addPromises = selectedUsersToAdd.map(async (userEmail) => {
          try {
            const currentPermissions = await fetchUserPermissions(userEmail);
            const currentCodes = currentPermissions.additionalCodes || [];
            const updatedCodes = [...new Set([...currentCodes, workspace.code])];
            
            await editUserPermissions(userEmail, {
              additionalCodes: updatedCodes,
              admin: false,
              sendTasks: false
            }, currentCodes);
          } catch (err) {
            console.error(`Error al agregar permisos para ${userEmail}:`, err);
          }
        });

        await Promise.all(addPromises);
      }

      const hasNameChange = editWorkspaceName.trim() && editWorkspaceName !== workspace.name;
      const hasIconChange = editWorkspaceIcon !== (workspace.icon || '');
      const prevPermisos = workspace.permisos == null ? {} : typeof workspace.permisos === 'string' ? (() => { try { return JSON.parse(workspace.permisos); } catch { return {}; } })() : workspace.permisos;
      const hasPermisosChange = BOARD_PERMISSION_KEYS.some(k => !!editWorkspaceRestrictions[k] !== !!prevPermisos[k]);
      if (hasNameChange || hasIconChange || hasPermisosChange) {
        const permisosPayload = Object.values(editWorkspaceRestrictions).some(Boolean) ? editWorkspaceRestrictions : null;
        await updateWorkspace(isEditing, {
          name: editWorkspaceName.trim() || workspace.name,
          icon: editWorkspaceIcon,
          permisos: permisosPayload
        });
      }

      // Finalizar
      handleFinishEdit();
    } catch (error) {
      console.error("Error al actualizar permisos:", error);
      setError('Error al actualizar permisos.');
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  // Finalizar el proceso de creación y resetear el formulario
  const handleFinish = () => {
    setNewWorkspaceName('');
    setNewWorkspaceRestrictions(getDefaultBoardPermissions());
    setSelectedIcon('');
    setSelectedUsersToAdd([]);
    setAddUserSearchTerm('');
    setGeneratedCode('');
    setCreatedWorkspaceCode(null);
    setIsCreating(false);
    setError('');
  };

  // Finalizar el proceso de edición y resetear el formulario
  const handleFinishEdit = () => {
    setIsEditing(null);
    setEditWorkspaceName('');
    setEditWorkspaceIcon('');
    setEditWorkspaceRestrictions(getDefaultBoardPermissions());
    setSelectedUsersToAdd([]);
    setSelectedUsersToRemove([]);
    setAddUserSearchTerm('');
    setRemoveUserSearchTerm('');
    setCurrentBoardUsers([]);
    setError('');
  };

  const startEditing = async (workspace) => {
    setIsEditing(workspace.id);
    setEditWorkspaceName(workspace.name);
    setEditWorkspaceIcon(workspace.icon || '');
    const defaults = getDefaultBoardPermissions();
    const raw = workspace.permisos;
    const parsed = raw == null ? {} : typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : raw;
    setEditWorkspaceRestrictions(
      BOARD_PERMISSION_KEYS.reduce((acc, key) => {
        acc[key] = parsed[key] === true;
        return acc;
      }, { ...defaults })
    );
    setSelectedUsersToAdd([]);
    setSelectedUsersToRemove([]);
    setAddUserSearchTerm('');
    setRemoveUserSearchTerm('');
    
    if (allUsers.length === 0) {
      await fetchAllUsers();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpdateWorkspace = async (e) => {
    e.preventDefault();
    if (!editWorkspaceName.trim()) return;
    
    // Set loading state if needed
    setError('');
    
    try {
      await updateWorkspace(isEditing, { 
        name: editWorkspaceName,
        icon: editWorkspaceIcon
      });
      
      // Reset edit state on success
      setIsEditing(null);
      setEditWorkspaceName('');
      setEditWorkspaceIcon('');
    } catch (error) {
      console.error('Error al actualizar tablero:', error);
      setError(error.message || 'Error al actualizar el tablero en DynamoDB');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteWorkspace = (id) => {
    if (workspaceData.length <= 1) {
      alert('No puedes eliminar el único tablero.');
      return;
    }

    if (window.confirm('¿Estás seguro de que deseas eliminar este tablero? Se perderán todas sus tareas.')) {
      deleteWorkspace(id);
    }
  };

  // Mostrar estado de carga mientras se obtienen los datos del API
  if (apiLoading) {
    return (
      <div className="workspace-manager">
        <div className="workspace-manager-header">
          <h2>Administrador de Tableros</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando datos de usuario...</p>
        </div>
      </div>
    );
  }

  // Mostrar mensaje de error si falla la llamada a la API
  if (apiError) {
    return (
      <div className="workspace-manager">
        <div className="workspace-manager-header">
          <h2>Administrador de Tableros</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="error-container">
          <div className="error-icon">!</div>
          <p>Error al cargar datos de usuario. Por favor, recarga la página o contacta con soporte.</p>
        </div>
      </div>
    );
  }

  // Mostrar estado de carga mientras se verifica el acceso a los workspaces
  if (workspacesLoading) {
    return (
      <div className="workspace-manager">
        <div className="workspace-manager-header">
          <h2>Administrador de Tableros</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Verificando acceso a tableros...</p>
        </div>
      </div>
    );
  }

  // Si estamos creando o editando, mostrar el modal correspondiente
  if (isCreating || isEditing) {
    const isEditMode = !!isEditing;
    // eslint-disable-next-line no-unused-vars
    const workspace = isEditMode ? workspaceData.find(ws => ws.id === isEditing) : null;
    
    return (
      <div className="workspace-manager">
        <div className="workspace-manager-header">
          <h2>Administrador de Tableros</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="new-workspace-form">
          {/* Campo nombre tablero en la parte superior */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label>nombre tablero</label>
            <input
              type="text"
              value={isEditMode ? editWorkspaceName : newWorkspaceName}
              onChange={(e) => isEditMode ? setEditWorkspaceName(e.target.value) : setNewWorkspaceName(e.target.value)}
              placeholder="nombre tablero"
              className="board-name-input"
            />
          </div>
          <div className="form-group workspace-restrictions-section" style={{ marginBottom: '16px' }}>
            <label>Restricciones del tablero (Permisos)</label>
            <small className="form-hint" style={{ display: 'block', marginBottom: '8px' }}>
              Si activas una opción, en este tablero solo podrán hacer esa acción los usuarios a los que hayas dado el permiso correspondiente en el Administrador de Permisos.
            </small>
            <div className="workspace-restrictions-grid">
              {BOARD_PERMISSION_KEYS.map(key => {
                const restrictions = isEditMode ? editWorkspaceRestrictions : newWorkspaceRestrictions;
                const setRestrictions = isEditMode ? setEditWorkspaceRestrictions : setNewWorkspaceRestrictions;
                return (
                  <div key={key} className="checkbox-container workspace-restriction-item">
                    <input
                      type="checkbox"
                      id={`restrict-${key}`}
                      checked={!!restrictions[key]}
                      onChange={(e) => setRestrictions(prev => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <label htmlFor={`restrict-${key}`}>{BOARD_RESTRICTION_LABELS[key]}</label>
                  </div>
                );
              })}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {isCreating ? (
            /* Modal de creación - Paso 2: Añadir Personas */
            <div className="step-2-container">
              <h3>Añadir Personas - Paso 2</h3>
              
              <div className="form-group">
                <label>Buscar usuarios:</label>
                <input
                  type="text"
                  value={addUserSearchTerm}
                  onChange={(e) => setAddUserSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre o email..."
                  className="user-search-input"
                />
              </div>

              {loadingUsers ? (
                <div className="loading-container">
                  <p>Cargando usuarios...</p>
                </div>
              ) : (
                <div className="users-list-container">
                  {filteredUsersToAdd.length > 0 ? (
                    <div className="users-list">
                      {filteredUsersToAdd.map((user, index) => {
                        const userEmail = user.email || user.user || '';
                        // eslint-disable-next-line no-unused-vars
                        const userName = user.name || userEmail;
                        const isSelected = selectedUsersToAdd.includes(userEmail);
                        
                        return (
                          <div 
                            key={index} 
                            className={`user-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleUserSelectionToAdd(user)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleUserSelectionToAdd(user)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="user-email">{userEmail}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-users-message">
                      {addUserSearchTerm ? 'No se encontraron usuarios' : 'No hay usuarios disponibles'}
                    </div>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={handleFinish}
                  disabled={isCreatingWorkspace}
                >
                  Atrás
                </button>
                <button 
                  type="button" 
                  className="create-btn"
                  onClick={handleAddPermissions}
                  disabled={isCreatingWorkspace || !newWorkspaceName.trim()}
                >
                  {isCreatingWorkspace ? 'Guardando...' : 'Finalizar'}
                </button>
              </div>
            </div>
          ) : (
            /* Modal de edición - Quitar Personas y Añadir Personas */
            <div className="edit-workspace-container">
              {/* Sección Quitar Personas */}
              <div className="remove-people-section">
                <h3>Quitar Personas -</h3>
                
                <div className="form-group">
                  <label>Buscar usuarios:</label>
                  <input
                    type="text"
                    value={removeUserSearchTerm}
                    onChange={(e) => setRemoveUserSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="user-search-input"
                  />
                </div>

                {loadingUsers ? (
                  <div className="loading-container">
                    <p>Cargando usuarios...</p>
                  </div>
                ) : (
                  <div className="users-list-container">
                    {filteredUsersToRemove.length > 0 ? (
                      <div className="users-list">
                        {filteredUsersToRemove.map((user, index) => {
                          const userEmail = user.email || user.user || '';
                          const isSelected = selectedUsersToRemove.includes(userEmail);
                          
                          return (
                            <div 
                              key={index} 
                              className={`user-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleUserSelectionToRemove(user)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleUserSelectionToRemove(user)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="user-email">{userEmail}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no-users-message">
                        {removeUserSearchTerm ? 'No se encontraron usuarios' : 'No hay usuarios con acceso'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sección Añadir Personas */}
              <div className="add-people-section">
                <h3>Añadir Personas -</h3>
                
                <div className="form-group">
                  <label>Buscar usuarios:</label>
                  <input
                    type="text"
                    value={addUserSearchTerm}
                    onChange={(e) => setAddUserSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="user-search-input"
                  />
                </div>

                {loadingUsers ? (
                  <div className="loading-container">
                    <p>Cargando usuarios...</p>
                  </div>
                ) : (
                  <div className="users-list-container">
                    {filteredUsersToAdd.length > 0 ? (
                      <div className="users-list">
                        {filteredUsersToAdd.map((user, index) => {
                          const userEmail = user.email || user.user || '';
                          const isSelected = selectedUsersToAdd.includes(userEmail);
                          
                          return (
                            <div 
                              key={index} 
                              className={`user-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleUserSelectionToAdd(user)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleUserSelectionToAdd(user)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="user-email">{userEmail}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no-users-message">
                        {addUserSearchTerm ? 'No se encontraron usuarios' : 'No hay usuarios disponibles'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={handleFinishEdit}
                  disabled={isCreatingWorkspace}
                >
                  Atrás
                </button>
                <button 
                  type="button" 
                  className="create-btn"
                  onClick={handleUpdatePermissions}
                  disabled={isCreatingWorkspace || !editWorkspaceName.trim()}
                >
                  {isCreatingWorkspace ? 'Guardando...' : 'Finalizar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vista principal: lista de tableros en cuadrícula
  return (
    <div className="workspace-manager">
      <div className="workspace-manager-header">
        <div className="header-left">
          <input
            type="text"
            placeholder="Buscar"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="workspace-search-input-top"
          />
        </div>
        <div className="header-right">
          <button 
            className="create-workspace-btn-top"
            onClick={handleStartCreating}
          >
            CREAR
          </button>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Cuadrícula de tableros */}
      <div className="workspace-grid">
        {filteredWorkspaces.map((workspace) => (
          <div key={workspace.id} className="workspace-grid-item">
            <div className="workspace-grid-name">{workspace.name}</div>
            <div className="workspace-grid-permisos">
              <span className="workspace-grid-permisos-label">Restricciones:</span>{' '}
              {(() => {
                const p = workspace.permisos;
                if (p == null) return 'Ninguna';
                const obj = typeof p === 'string' ? (() => { try { return JSON.parse(p); } catch { return {}; } })() : p;
                const count = obj && typeof obj === 'object' ? Object.values(obj).filter(Boolean).length : 0;
                return count ? `${count} activas` : 'Ninguna';
              })()}
            </div>
            <button 
              className="workspace-grid-edit-btn"
              onClick={() => startEditing(workspace)}
              title="Editar tablero"
            >
              Editar
            </button>
          </div>
        ))}
        
        {filteredWorkspaces.length === 0 && searchTerm && (
          <div className="no-results-message">
            No se encontraron tableros con el término "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkspaceManager; 